import { useCallback } from "react"
import { Copy, ExternalLink } from "lucide-react"
import { toast } from "@/components/ui/use-toast"

const DEV_MODE = import.meta.env.VITE_DEV_MODE === "true"

function getExplorerUrl(txHash: string): string {
  if (DEV_MODE) {
    // No explorer for local hardhat
    return ""
  }
  return `https://bscscan.com/tx/${txHash}`
}

function shortenTxHash(hash: string): string {
  if (!hash) return ""
  return `${hash.slice(0, 6)}...${hash.slice(-4)}`
}

interface TxLinkProps {
  txHash: string
}

function TxLink({ txHash }: TxLinkProps) {
  const explorerUrl = getExplorerUrl(txHash)
  const shortHash = shortenTxHash(txHash)

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(txHash)
    } catch (err) {
      console.error("Failed to copy:", err)
    }
  }

  return (
    <div className="flex items-center gap-2 mt-1">
      {explorerUrl ? (
        <a
          href={explorerUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors font-mono"
        >
          {shortHash}
          <ExternalLink className="h-3 w-3" />
        </a>
      ) : (
        <span className="text-xs text-muted-foreground font-mono">
          {shortHash}
        </span>
      )}
      <button
        onClick={handleCopy}
        className="p-1 hover:bg-muted rounded transition-colors"
        title="Copy transaction hash"
      >
        <Copy className="h-3 w-3 text-muted-foreground hover:text-foreground" />
      </button>
    </div>
  )
}

export type TransactionAction =
  | "Play"
  | "Claim Rewards"
  | "Register Auto-Play"
  | "Cancel Auto-Play"
  | "Set Auto-Play Squares"
  | "Top Up Auto-Play"
  | "Approve"
  | "Swap"
  | "Stake"
  | "Unstake"
  | "Claim Staking Rewards"
  | "Bridge"
  | "Top Up Gas"
  | "Start Stream"
  | "Top Up Stream"
  | "Cancel Stream"
  | "Purchase Vanity"
  | "Set Resale Price"
  | "Set Preferred Vanity"
  | "Set Referrer"
  | "Fund Stream"
  | "Set Profile Picture"
  | "Remove Profile Picture"
  | "Direct Transfer"
  | "Claim stWELEPHANT"
  | "Delete Message"
  | "Hide Message"
  | "Show Message"
  | "Toggle Visibility"
  | "Clap"
  | "Set Default Visibility"
  | "Set Cover Image"
  | "Remove Cover Image"
  | "Set Message"
  | "Verify Stream"
  | "Unverify Stream"
  | "Raffle Send"

export function useTransactionToast() {
  const showTxPending = useCallback((action: TransactionAction, txHash: string) => {
    const { id, update, dismiss } = toast({
      title: `${action}`,
      description: <TxLink txHash={txHash} />,
      status: "pending",
    })

    return {
      id,
      dismiss,
      success: () => {
        update({
          title: `${action} Successful`,
          description: <TxLink txHash={txHash} />,
          status: "success",
        })
      },
      error: (errorMessage?: string) => {
        update({
          title: `${action} Failed`,
          description: (
            <div>
              {errorMessage && (
                <p className="text-xs text-destructive mb-1">{errorMessage}</p>
              )}
              <TxLink txHash={txHash} />
            </div>
          ),
          status: "error",
        })
      },
    }
  }, [])

  const showTxError = useCallback((action: TransactionAction, errorMessage: string) => {
    toast({
      title: `${action} Failed`,
      description: <p className="text-xs">{errorMessage}</p>,
      status: "error",
    })
  }, [])

  return {
    showTxPending,
    showTxError,
  }
}
